<template>
  <div
    class="jade-popover"
    v-dismiss="computedDismiss"
  >
    <div
      ref="referenceRef"
      role="presentation"
      aria-label="trigger"
      class="jade-popover__trigger"
      data-testid="trigger"
      :aria-describedby="defineFloatingID"
      @click="toggleOpen"
    >
      <slot name="trigger"></slot>
    </div>
    <transition name="fade">
      <div
        ref="floatingRef"
        v-show="isVisible"
        tabindex="-1"
        class="absolute top-0 left-0 z-[99999]"
        data-testid="floating"
        :style="{ zIndex }"
        :id="defineFloatingID"
      >
        <slot></slot>
      </div>
    </transition>
  </div>
</template>

<script>
import { computePosition, offset, shift, autoPlacement } from '@floating-ui/dom'
import dismiss from '@utils/dismiss.directives'
import { toFixed } from '@utils/number.utils'
import { isMobile } from '@utils/device.utils'
import PLACEMENT_ENUM from '../enums/alignment.enum'

export default {
  name: 'JadePopover',
  directives: {
    dismiss
  },
  props: {
    open: Boolean,
    zIndex: Number,
    allowedPlacements: {
      type: Array,
      default: () => [
        PLACEMENT_ENUM.BOTTOM_START,
        PLACEMENT_ENUM.TOP_END,
        PLACEMENT_ENUM.BOTTOM_END,
        PLACEMENT_ENUM.TOP_START
      ]
    },
    offset: {
      type: Number,
      default: 5
    },
    alignment: {
      type: String,
      default: 'end',
      validation: v => ['end', 'start'].includes(v)
    },
    closeOn: {
      type: Array,
      default: () => ['outside']
    }
  },
  data () {
    return {
      isVisible: false,
      referenceRef: null,
      floatingRef: null
    }
  },
  computed: {
    defineFloatingID () {
      return `floating-ui-${new Date().getTime()}`
    },
    computedDismiss () {
      return {
        outsidePressEvent: 'mousedown',
        outsidePress: this.closeOn.includes('outside'),
        escapeKey: this.closeOn.includes('esc'),
        event: () => {
          this.$emit('closePopover')
        }
      }
    }
  },
  watch: {
    open: {
      async handler (isOpen) {
        this.isVisible = isOpen

        if (!isOpen) {
          return
        }

        await this.calculatePosition()

        // Resolve the issue of relative parents
        this.$nextTick(async () => {
          const hasRelativeParents = this.hasRelativeParents(this.referenceRef)

          if (hasRelativeParents) {
            this.floatingRef.style.position = 'fixed'
            await this.calculatePosition()
          }

          this.handleScrollY()
        })
      },
      immediate: true
    }
  },
  async mounted () {
    const { referenceRef, floatingRef } = this.$refs
    this.referenceRef = referenceRef
    this.floatingRef = floatingRef
  },
  methods: {
    toggleOpen (value) {
      this.$emit('toggleOpen', !!value)
    },
    async calculatePosition () {
      const { x, y } = await computePosition(
        this.referenceRef,
        this.floatingRef,
        {
          middleware: [
            offset(this.offset),
            autoPlacement({
              allowedPlacements: this.allowedPlacements,
              alignment: this.alignment
            }),
            shift()
          ]
        }
      )

      this.handleClamp({ x, y })
    },

    handleClamp ({ x, y }) {
      const TARGET_RECT = this.referenceRef.getBoundingClientRect()
      const POPOVER_RECT = this.floatingRef.getBoundingClientRect()

      const { isOutsideScreenRight, isOutsideScreenLeft } =
        this.handleOutsideElements({ x, popoverWidth: POPOVER_RECT.width })

      let popoverAxleX = x

      if (isOutsideScreenLeft) {
        popoverAxleX = TARGET_RECT.x
      }

      if (isOutsideScreenRight) {
        popoverAxleX = TARGET_RECT.left - POPOVER_RECT.width + TARGET_RECT.width
      }

      Object.assign(this.floatingRef?.style ?? {}, {
        minWidth: `${TARGET_RECT.width}px`,
        transform: `translate(${toFixed(popoverAxleX)}px,${y}px)`
      })

      return { POPOVER_RECT, TARGET_RECT }
    },

    handleOutsideElements ({ x, popoverWidth }) {
      const SCREEN_WIDTH = document
        .querySelector('#core-main')
        .getBoundingClientRect().width
      const SIDEBAR_WIDTH = document
        .querySelector('#core-sidebar')
        .getBoundingClientRect().width

      const MAIN_CONTENT_WIDTH = isMobile()
        ? SCREEN_WIDTH
        : SCREEN_WIDTH - SIDEBAR_WIDTH

      const isOutsideScreenRight = x + popoverWidth > MAIN_CONTENT_WIDTH
      const isOutsideScreenLeft = x + popoverWidth < MAIN_CONTENT_WIDTH

      return { isOutsideScreenRight, isOutsideScreenLeft }
    },

    handleScrollY () {
      if (this.floatingRef?.style.position !== 'fixed') {
        return
      }
      let lastPositionScroll = window.pageYOffset
      window.onscroll = () => {
        const currentPositionScroll = window.pageYOffset

        if (currentPositionScroll > lastPositionScroll) {
          this.isVisible = false
          this.$emit('closePopover')
        }

        lastPositionScroll = currentPositionScroll
      }
    },

    hasRelativeParents (element) {
      let currentElement = element.parentElement
      let count = 0
      while (currentElement && count <= 3) {
        count++
        const position = window.getComputedStyle(currentElement).position
        if (position === 'relative') {
          return true
        }
        currentElement = currentElement.parentElement
      }

      return false
    }
  }
}
</script>

<style scoped>
@import '@stone-payments/jade/css/popover.css';
</style>
